import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { takeSsoReturnTo, useAuth } from '@/lib/auth';

/**
 * Where an identity provider sends the browser back to.
 *
 * The provider redirects here with `code` and `state` in the query string; this
 * page hands them to the API and gets a session in a response body. The
 * alternative — the provider redirecting straight into the API, which then
 * redirects here with tokens attached — would leave those tokens in browser
 * history and in every proxy log between here and there.
 *
 * One route serves both ceremonies. The signed state says which it was, so the
 * provider in the path is only there to give each provider its own redirect URI
 * to register.
 */
export function SsoCallbackPage() {
  const [params] = useSearchParams();
  const { completeSso, completeSsoConnect, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // React runs effects twice in development, and an authorization code is
  // single use at the provider: the second attempt would fail and replace a
  // successful sign-in with an error.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const denied = params.get('error');

    if (denied) {
      // The provider's own words are more useful than ours: it knows whether
      // the person cancelled, or the application is not approved for them.
      setError(params.get('error_description') ?? describe(denied));
      return;
    }
    if (!code || !state) {
      setError('That sign-in link is incomplete. Start again from the sign-in page.');
      return;
    }

    // Connecting an account happens from inside the dashboard, so a session is
    // already in hand; signing in is the case where there is none.
    const finish = user
      ? completeSsoConnect(code, state).then(() => navigate('/account', { replace: true }))
      : completeSso(code, state).then((challenge) => {
          const destination = takeSsoReturnTo();
          if (!challenge) {
            navigate(destination, { replace: true });
            return;
          }
          // The account has an authenticator, so nobody is signed in yet. The
          // sign-in page already asks for the code; the token travels in
          // history state rather than the URL, where it would be logged.
          const { pathname, search, hash } = new URL(destination, window.location.origin);
          navigate('/login', {
            replace: true,
            state: { mfaToken: challenge.mfaToken, from: { pathname, search, hash } },
          });
        });

    finish.catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'That sign-in could not be completed.'),
    );
  }, [params, user, completeSso, completeSsoConnect, navigate]);

  return (
    <div className="flex min-h-screen flex-col px-4">
      <main className="grid flex-1 place-items-center py-8">
        <Card className="w-full max-w-sm">
          <CardHeader className="items-center text-center">
            <span className="grid size-10 place-items-center rounded-lg bg-primary text-primary-foreground">
              <AlertTriangle className="size-5" />
            </span>
            <CardTitle className="text-xl">
              {error ? 'Sign-in failed' : 'Signing you in…'}
            </CardTitle>
            <CardDescription>
              {error ?? 'Finishing up with your identity provider.'}
            </CardDescription>
          </CardHeader>
          {error && (
            <CardContent>
              <Button asChild className="w-full">
                <Link to={user ? '/account' : '/login'}>
                  {user ? 'Back to settings' : 'Try again'}
                </Link>
              </Button>
            </CardContent>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}

/** A fallback for the OAuth error codes a provider may send without a description. */
function describe(code: string): string {
  switch (code) {
    case 'access_denied':
      return 'You cancelled the sign-in, or your administrator has not approved Outpost.';
    case 'invalid_client':
    case 'unauthorized_client':
      return 'Outpost is not registered correctly with this provider. Ask an administrator to check its client id and secret.';
    default:
      return `Your identity provider refused the sign-in (${code}).`;
  }
}
